/**
 * Phone Floor catalog rail — favorites/recent, not full matrix — plan/35.
 */
import { normalizeScan } from "./catalog-lookup-math";

/** Max tiles on compact (phone / stacked) rail. */
export const POS_PHONE_RAIL_MAX = 12;

/** Cap stored recent product ids (MRU). */
export const POS_RECENT_MAX = 24;

/** Cap stored favorite product ids. */
export const POS_FAVORITE_MAX = 48;

/**
 * Favorites first (stable order), then recent not already favorited.
 * Drops ids missing from catalog.
 */
export function orderedRailIds(
  favoriteIds: readonly string[],
  recentIds: readonly string[],
  catalogIds: ReadonlySet<string>,
  max = POS_PHONE_RAIL_MAX,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of favoriteIds) {
    if (out.length >= max) break;
    if (!catalogIds.has(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  for (const id of recentIds) {
    if (out.length >= max) break;
    if (!catalogIds.has(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function pickCatalogByIds<T extends { id: string }>(
  catalog: readonly T[],
  ids: readonly string[],
): T[] {
  const byId = new Map(catalog.map((p) => [p.id, p]));
  const out: T[] = [];
  for (const id of ids) {
    const p = byId.get(id);
    if (p) out.push(p);
  }
  return out;
}

/** Push id to front of MRU list (deduped). */
export function pushRecentId(
  recentIds: readonly string[],
  productId: string,
  max = POS_RECENT_MAX,
): string[] {
  const next = [
    productId,
    ...recentIds.filter((id) => id !== productId),
  ];
  return next.slice(0, max);
}

/** Toggle favorite; new favorites prepend; respects max. */
export function toggleFavoriteId(
  favoriteIds: readonly string[],
  productId: string,
  max = POS_FAVORITE_MAX,
): string[] {
  if (favoriteIds.includes(productId)) {
    return favoriteIds.filter((id) => id !== productId);
  }
  return [productId, ...favoriteIds].slice(0, max);
}

/**
 * Browse / live-search filter — name, sku, barcode (contains).
 * Empty query → full catalog order preserved.
 */
export function filterCatalogBrowse<
  T extends {
    name: string;
    sku: string | null;
    barcode: string | null;
  },
>(catalog: readonly T[], rawQuery: string): T[] {
  const q = normalizeScan(rawQuery).toLowerCase();
  if (!q) return [...catalog];
  return catalog.filter((p) => {
    if (p.name.toLowerCase().includes(q)) return true;
    if (p.sku != null && p.sku.toLowerCase().includes(q)) return true;
    if (p.barcode != null && p.barcode.toLowerCase().includes(q)) return true;
    return false;
  });
}

/** Compact Floor when cart stacks under catalog (below Tailwind `lg`). */
export const POS_COMPACT_MQ = "(max-width: 1023px)";
